import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';

import ForwardIcon from '@mui/icons-material/ArrowForwardIos';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { makeStyles } from '@mui/styles';

import { translate } from '@vestahealthcare/common/i18n';
import {
  ClinicalMember,
  MemberDashboard,
  Patient,
} from '@vestahealthcare/common/models';

import {
  BackgroundColors,
  Card,
  Colors,
  Fonts,
  Tabs,
  Tooltip,
} from 'styleguide-v2';
import { TabsItem } from 'styleguide-v2/src/components/Tabs';

import Session from 'dash/src/services/SessionServices';

import { RecentSkilledTable } from './RecentSkilledTable';
import { RecentTocTable } from './RecentTocTable';

type Props = {
  className?: string;
  member: Patient | ClinicalMember;
  data?: MemberDashboard;
};

const useStyles = makeStyles({
  container: {
    '&&': {
      borderRadius: '8px',
    },
  },
  dataContainer: {
    '&&': {
      paddingTop: '1rem',
    },
  },
  subContainer: {
    display: 'flex',
    flexFlow: 'column',
    padding: '2.4rem 3.2rem',
  },
  tabs: {
    padding: '0 3.2rem',
    borderBottom: `1px solid ${BackgroundColors.grayLine}`,
  },
  title: {
    alignItems: 'center',
    display: 'flex',

    '&&': {
      fontFamily: Fonts.fontFamily,
      fontSize: `calc(${Fonts.fontSize} * 1.25)`,
      fontWeight: 500,
      margin: 0,
    },
  },
  titleContainer: {
    '&&': {
      paddingBottom: '1rem',
    },
  },
});

const MEMBER_DASHBOARD_TOC = 'member-dashboard-toc';
const MEMBER_DASHBOARD_SKILLED = 'member-dashboard-skilled';
const TABS = [
  {
    value: MEMBER_DASHBOARD_TOC,
    label: translate('memberProfile.dashboard.tabs.toc'),
  },
  {
    value: MEMBER_DASHBOARD_SKILLED,
    label: translate('memberProfile.dashboard.tabs.skilled'),
  },
];

export const RecentTocSkilledCard = ({ className, data, member }: Props) => {
  const styles = useStyles();
  const [tabs, setTabs] = useState<TabsItem[]>([]);

  const shouldShowLink = Session.actingUser.showMemberLink;
  const memberId = useMemo(
    () => (member instanceof Patient ? member?.id : member.memberId),
    [member],
  );
  const openNewTab = useMemo(() => !(member instanceof Patient), [member]);

  const [selectedTab, setSelectedTab] = useState<string>(MEMBER_DASHBOARD_TOC);

  useEffect(() => {
    if (!data) {
      setTabs([...TABS]);
    } else {
      setTabs([
        {
          value: TABS[0].value,
          label: `${TABS[0].label} (${data.toc.length})`,
        },
        {
          value: TABS[1].value,
          label: `${TABS[1].label} (${data.skilled.length})`,
        },
      ]);
    }
  }, [data?.toc?.length, data?.skilled?.length]);

  return (
    <Card className={classNames(className, styles.container)}>
      <div className={classNames(styles.subContainer, styles.titleContainer)}>
        <div className="flex spaced">
          <span className={styles.title}>
            {translate('memberProfile.dashboard.recentTocSkilled')}
            &nbsp;
            <Tooltip
              text={translate(
                'memberProfile.dashboard.recentTocSkilledTooltip',
              )}
            >
              <InfoOutlinedIcon htmlColor={Colors.iconGray} />
            </Tooltip>
          </span>
          {shouldShowLink && (
            <a
              className="flex middle min-gap"
              href={`#/patients/${memberId}/toc-list`}
              style={{ color: Colors.textGreen, fontWeight: 500 }}
              rel="noreferrer"
              target={openNewTab ? '_blank' : undefined}
            >
              {translate('memberProfile.dashboard.goToTocSkilled')}
              <ForwardIcon fontSize="small" />
            </a>
          )}
        </div>
      </div>
      <Tabs
        className={styles.tabs}
        items={tabs}
        onChange={setSelectedTab}
        value={selectedTab}
      />
      <div className={classNames(styles.dataContainer, styles.subContainer)}>
        {selectedTab === MEMBER_DASHBOARD_TOC && (
          <RecentTocTable data={data?.toc} />
        )}
        {selectedTab === MEMBER_DASHBOARD_SKILLED && (
          <RecentSkilledTable data={data?.skilled} />
        )}
      </div>
    </Card>
  );
};
